import { DndContext } from '@dnd-kit/core';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { Box, IconButton } from '@mui/material';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  type ImperativePanelGroupHandle,
  type ImperativePanelHandle,
  Panel,
  PanelGroup,
  type PanelGroupProps,
  type PanelOnCollapse,
  type PanelOnExpand,
  type PanelOnResize,
  type PanelProps,
  PanelResizeHandle,
} from 'react-resizable-panels';

import DndOverlay from '@/Components/dnd/DndOverlay';
import {
  type PanelListeners,
  type PanelName,
  PanelProvider,
  type PanelProviderType,
  usePanelSizeAtom,
} from './panel.utils';

export type PanelCallbacks = {
  onCollapse?: PanelOnCollapse;
  onExpand?: PanelOnExpand;
  onResize?: PanelOnResize;
};

type CustomHandleProps = {
  isCollapsed: boolean;
  onToggle: () => void;
};

const CustomHandle = ({ isCollapsed, onToggle }: CustomHandleProps) => (
  <PanelResizeHandle style={{ display: 'flex', alignItems: 'center' }}>
    <div style={{ cursor: 'pointer' }}>
      <IconButton
        onClick={onToggle}
        size="small"
        sx={{
          zIndex: 10,
          opacity: 0.7,
          '&:hover': { opacity: 1 },
          color: 'black',
          // hide for now
          position: 'absolute',
          display: 'none',
        }}
      >
        {isCollapsed ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
    </div>
    <Box
      sx={{
        width: '5px',
        height: 'calc(100% - 10px)',
        border: '1px solid #5e5e5e',
        cursor: 'col-resize',
        opacity: 0.7,
        transition: 'all 0.2s',
        '&:hover': {
          opacity: 1,
          height: 'calc(100% - 2px)',
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
        },
      }}
    />
  </PanelResizeHandle>
);

export type PanelComponentProps = PanelProviderType;
export type PanelComponentWithProps = (props: PanelComponentProps) => React.ReactNode;

type Props = {
  // panel group props
  panelGroupProps?: Omit<PanelGroupProps, 'direction'>;

  // panel props
  leftPanelProps?: PanelProps;
  rightPanelProps?: PanelProps;

  // function component with ctx or component
  leftComponent?: React.ReactNode | PanelComponentWithProps;
  rightComponent?: React.ReactNode | PanelComponentWithProps;

  children?: React.ReactNode;
};

const Wrapper = (props: Props, forwardRef: React.ForwardedRef<PanelProviderType>) => {
  const listeners = useRef<PanelListeners>(new Map());

  const panelGroupRef = useRef<ImperativePanelGroupHandle | null>(null);
  const leftPanelRef = useRef<ImperativePanelHandle | null>(null);
  const rightPanelRef = useRef<ImperativePanelHandle | null>(null);

  const leftPanelSize = useRef<number | null>(leftPanelRef?.current?.getSize() ?? null);
  const rightPanelSize = useRef<number | null>(rightPanelRef?.current?.getSize() ?? null);

  const [, setLeftAtom] = usePanelSizeAtom('left');
  const [, setRightAtom] = usePanelSizeAtom('right');

  // set the initial size of the panels
  useEffect(() => {
    setRightAtom(rightPanelRef?.current?.getSize() ?? props?.rightPanelProps?.defaultSize ?? 0);
    setLeftAtom(leftPanelRef?.current?.getSize() ?? props?.leftPanelProps?.defaultSize ?? 0);
  }, []);

  const [isRightPanelCollapsed, setIsRightPanelCollapsed] = useState(false);
  const [rightPanelOriginalSize, setRightPanelOriginalSize] = useState(30);

  const toggleRightPanel = useCallback(() => {
    if (isRightPanelCollapsed) {
      rightPanelRef.current?.expand(rightPanelOriginalSize);
      setIsRightPanelCollapsed(false);
    } else {
      setRightPanelOriginalSize(rightPanelSize?.current ?? 30);
      rightPanelRef.current?.collapse();
      setIsRightPanelCollapsed(true);
    }
  }, [isRightPanelCollapsed, rightPanelOriginalSize]);

  /**
   * register the callbacks for the panels
   */
  const panelCallbackProps = useCallback(
    (name: PanelName) => {
      return {
        onResize: (size: number, prevSize: number | undefined) => {
          if (name === 'right') {
            rightPanelSize.current = size;
            setRightAtom(size);
            setIsRightPanelCollapsed(size <= 20);
            if (!isRightPanelCollapsed) {
              setRightPanelOriginalSize(size);
            }
          }

          if (name === 'left') {
            leftPanelSize.current = size;
            setLeftAtom(size);
          }

          (listeners.current.get(name)?.get?.('onResize') as Array<PanelCallbacks['onResize']>)?.forEach?.((cb) =>
            cb?.(size, prevSize)
          );
        },
        onCollapse: () => {
          (listeners.current.get(name)?.get('onCollapse') as Array<PanelCallbacks['onCollapse']>)?.forEach((cb) =>
            cb?.()
          );
        },
        onExpand: () => {
          (listeners.current.get(name)?.get('onExpand') as Array<PanelCallbacks['onExpand']>)?.forEach((cb) => cb?.());
        },
      } as PanelCallbacks;
    },
    [listeners]
  );

  const panelCtxValue = useMemo(() => {
    return {
      panelGroup: panelGroupRef,
      leftPanel: leftPanelRef,
      leftPanelSize: leftPanelSize,
      rightPanel: rightPanelRef,
      listeners: listeners,
    };
  }, [panelGroupRef, leftPanelRef, leftPanelSize, rightPanelRef, listeners]);

  useImperativeHandle(forwardRef, () => panelCtxValue, [panelCtxValue]);

  const LeftComponent = useMemo(() => {
    if (
      typeof props?.leftComponent === 'function' &&
      panelCtxValue &&
      props?.leftComponent !== undefined &&
      props?.leftComponent !== null
    ) {
      return () => <>{(props.leftComponent as (props: PanelProviderType) => React.ReactNode)(panelCtxValue)}</>;
    }
    if (props?.leftComponent) {
      return () => <>{props.leftComponent as React.ReactNode}</>;
    }

    return () => <></>;
  }, [props?.leftComponent, panelCtxValue]);

  const RightComponent = useMemo(() => {
    if (
      typeof props?.rightComponent === 'function' &&
      panelCtxValue &&
      props?.rightComponent !== undefined &&
      props?.rightComponent !== null
    ) {
      return () => <>{(props.rightComponent as (props: PanelProviderType) => React.ReactNode)(panelCtxValue)}</>;
    }
    if (props?.rightComponent) {
      return () => <>{props.rightComponent as React.ReactNode}</>;
    }

    return () => <></>;
  }, [props?.rightComponent, panelCtxValue]);

  return (
    <PanelProvider.Provider value={panelCtxValue}>
      <DndContext>
        <PanelGroup {...props?.panelGroupProps} ref={panelGroupRef} direction="horizontal">
          <Panel
            defaultSize={70}
            minSize={30}
            collapsible={false} // Make the left panel non-collapsible
            {...props?.leftPanelProps}
            ref={leftPanelRef}
            {...panelCallbackProps('left')}
            style={props?.leftPanelProps?.style}
          >
            <LeftComponent />
          </Panel>

          <CustomHandle isCollapsed={isRightPanelCollapsed} onToggle={toggleRightPanel} />

          <Panel
            defaultSize={30}
            minSize={1} // Make sure this is not less than collapsedSize
            collapsible={true}
            collapsedSize={1} // Make sure this is not greater than minSize
            {...props?.rightPanelProps}
            ref={rightPanelRef}
            {...panelCallbackProps('right')}
            style={props?.rightPanelProps?.style}
          >
            <RightComponent />
          </Panel>
        </PanelGroup>
        <DndOverlay />
      </DndContext>
    </PanelProvider.Provider>
  );
};

const PanelWrapper = forwardRef(Wrapper);

export { PanelWrapper as PanelComponent };

export type { Props as PanelWrapperProps };

export const usePanelWrapperRef = () => {
  return useRef<PanelProviderType>(null);
};
